import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, CheckCircle } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { needsConfirmation } from "../utils/autopay";

import { API_BASE, apiFetch } from "../api";

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const AutopayCheckModal = () => {
  const { tokens, fonts } = useTheme();
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    apiFetch(`${API_BASE}/bills`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((bills) => setItems(bills.filter((b) => needsConfirmation(b))))
      .catch(() => {});
  }, []);

  const handleConfirmPaid = (bill) => {
    setItems((prev) => prev.filter((b) => b.id !== bill.id));
    apiFetch(`${API_BASE}/bills/${bill.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "Paid" }),
    }).catch(() => {});
  };

  if (dismissed || items.length === 0) return null;

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        <div style={styles.header}>
          <h3 style={{ ...styles.title, fontFamily: fonts.display, color: tokens.textPrimary }}>
            Did these autopay bills go through?
          </h3>
          <button onClick={() => setDismissed(true)} style={{ ...styles.closeBtn, color: tokens.textMuted }} aria-label="Dismiss">
            <X size={18} />
          </button>
        </div>
        <p style={{ ...styles.subtitle, color: tokens.textMuted }}>
          Their pay day has passed but they're still marked pending.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "16px 0" }}>
          {items.map((bill) => (
            <div
              key={bill.id}
              style={{ ...styles.row, backgroundColor: tokens.neutralSoft }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{bill.payee}</span>
                <span style={{ fontSize: 12, color: tokens.textMuted }}>
                  Due {formatDate(bill.dueDate)} &middot; ₹{bill.amount.toLocaleString()}
                </span>
                {!!bill.description && (
                  <span style={{ fontSize: 12, color: tokens.textMuted }}>{bill.description}</span>
                )}
              </div>
              <button
                onClick={() => handleConfirmPaid(bill)}
                style={{ ...styles.confirmBtn, backgroundColor: tokens.positive, color: tokens.accentContrast }}
              >
                <CheckCircle size={14} strokeWidth={2.6} />
                Paid
              </button>
            </div>
          ))}
        </div>

        <div style={styles.footer}>
          <Link to="/action-items" onClick={() => setDismissed(true)} style={{ ...styles.reviewLink, color: tokens.accent }}>
            Review all in Action Items
          </Link>
          <button onClick={() => setDismissed(true)} style={{ ...styles.laterBtn, color: tokens.textMuted }}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: 20,
  },
  modal: {
    borderRadius: 18,
    padding: "24px 26px",
    width: "100%",
    maxWidth: 440,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    flexShrink: 0,
  },
  subtitle: {
    fontSize: 13.5,
    margin: "6px 0 0",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderRadius: 12,
    gap: 12,
  },
  confirmBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    borderRadius: 8,
    padding: "7px 12px",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 4,
  },
  reviewLink: {
    fontSize: 13,
    fontWeight: 700,
    textDecoration: "none",
  },
  laterBtn: {
    background: "transparent",
    border: "none",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default AutopayCheckModal;
